import type { PathwayCheck, PathwayId } from './pathway-types.ts';
import { PATHWAY_QUESTIONS } from './pathways.ts';
import { resourceCategory } from './resource-categories.ts';

const programs=['cec','fsw','fst'];
const reportId=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const reportTargets=new Set(['materials','programs','dr-report-content','dr-key-information','dr-crs-overview','dr-score-breakdown','dr-improvements','dr-ee-eligibility','dr-extracted-facts']);
const targets=new Set([
  ...PATHWAY_QUESTIONS.map(question=>`halo-condition-${question.field}`),
  ...['age','marital','spouse-situation','education','education-qualification','canadian-credential','credential-level','first-language','has-french','canadian-work','foreign-work','teer','nomination','sibling','trade-certificate','spouse-education','spouse-education-qualification','spouse-language','spouse-work','fsw-spouse-language'].map(field=>`halo-${field}`),
  ...['english','french','spouse','fsw-spouse'].flatMap(language=>['test','listening','reading','writing','speaking'].map(field=>`halo-${language}-${field}`)),
  ...['about','education','language','work','additional','spouse'].map(section=>`halo-section-${section}`),
  ...['general','cec','fsw','fst','funds','adaptability'].map(group=>`halo-qualify-${group}`),
  'halo-program-supplement',
]);

export function assessmentLocation(program:PathwayId|null=null,target?:string,view:'crs'|'pathways'='pathways'):string {
  return `${view==='crs'?'/assessment':'/assessment/results'}${view==='pathways'&&program?`?program=${program}`:''}${target&&targets.has(target)?`#${target}`:''}`;
}

function safeAssessmentLocation(value:string|null):string|null {
  if(value==='/materials') return value;
  if(!value||/[\\\x00-\x20\x7f]/.test(value)||!/^\/(?:assessment(?:\/results)?|documents)(?:[?#]|$)/.test(value)) return null;
  const url=new URL(value,'https://halo.invalid');
  if(!['/assessment','/assessment/results','/documents'].includes(url.pathname)||url.origin!=='https://halo.invalid'||value!==`${url.pathname}${url.search}${url.hash}`) return null;
  const entries=[...url.searchParams.entries()];
  if(new Set(entries.map(([key])=>key)).size!==entries.length||entries.some(([key])=>!['program','report','from'].includes(key))||url.pathname==='/assessment'&&entries.length) return null;
  if(url.searchParams.has('program')&&(!programs.includes(url.searchParams.get('program')!)||url.pathname==='/documents')) return null;
  const savedReport=url.searchParams.get('report');
  if(savedReport!==null&&!reportId.test(savedReport)) return null;
  if(url.searchParams.has('from')&&(!savedReport||!['account','reports'].includes(url.searchParams.get('from')!))) return null;
  if(url.pathname==='/documents'&&!savedReport&&url.hash&&!reportTargets.has(url.hash.slice(1))) return null;
  if(url.hash&&!targets.has(url.hash.slice(1))&&!(url.pathname!=='/assessment'&&reportTargets.has(url.hash.slice(1)))) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Only navigation identifiers are carried between pages, never profile values. */
export function assessmentReturn(search:string):string|null {
  return safeAssessmentLocation(new URLSearchParams(search).get('returnTo'));
}

/** Resource links carry a restorable report location, never an upload's in-memory data. */
export function withReportReturn(href:string,reportLocation:string):string {
  const returnTo=safeAssessmentLocation(reportLocation);
  if(!returnTo||!/^\/resources(?:[/?#]|$)/.test(href))return href;
  const url=new URL(href,'https://halo.invalid');
  if(url.origin!=='https://halo.invalid'||!/^\/resources(?:\/[^/]+)?$/.test(url.pathname))return href;
  url.searchParams.set('returnTo',returnTo);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function resourceLibraryHref(returnTo?:string|null,category?:string|null,query?:string):string {
  const safe=safeAssessmentLocation(returnTo??null);
  const params=new URLSearchParams();
  if(safe) params.set('returnTo',safe);
  if(resourceCategory(category)) params.set('category',category!);
  if(query?.trim()) params.set('q',query.trim().slice(0,160));
  return `/resources${params.size?`?${params}`:''}`;
}

function safeLibraryLocation(value:string|null):string|null {
  if(!value||!/^\/resources(?:\?|$)/.test(value)) return null;
  const url=new URL(value,'https://halo.invalid');
  if(url.origin!=='https://halo.invalid'||url.pathname!=='/resources'||url.hash) return null;
  const entries=[...url.searchParams.entries()];
  if(new Set(entries.map(([key])=>key)).size!==entries.length||entries.some(([key])=>!['q','category','returnTo'].includes(key))) return null;
  if(url.searchParams.has('category')&&!resourceCategory(url.searchParams.get('category'))) return null;
  if((url.searchParams.get('q')?.length??0)>160) return null;
  if(url.searchParams.has('returnTo')&&!safeAssessmentLocation(url.searchParams.get('returnTo'))) return null;
  return `${url.pathname}${url.search}`;
}

/** Browsing context is separate from an applicant's assessment return target. */
export function resourceLibraryReturn(search:string):string|null {
  return safeLibraryLocation(new URLSearchParams(search).get('from'));
}

/** Public resource locations never include a user's answers. */
export function resourceHref(id: string, section?: string,returnTo?:string|null,libraryLocation?:string|null): string {
  const safe=safeAssessmentLocation(returnTo??null);
  const params=[];
  if(safe) params.push(`returnTo=${encodeURIComponent(safe)}`);
  const from=safeLibraryLocation(libraryLocation??null);
  if(from) params.push(`from=${encodeURIComponent(from)}`);
  return `/resources/${encodeURIComponent(id)}${params.length?`?${params.join('&')}`:''}${section ? `#${encodeURIComponent(section)}` : ''}`;
}

/** Read a sourced chapter and return to the field; only navigation IDs enter the URL. */
export function assessmentHelpHref(target:string,program:PathwayId|null=null,test?:string):string {
  const returnTo=assessmentLocation(program,target,program?'pathways':'crs');
  const question=PATHWAY_QUESTIONS.find(item=>`halo-condition-${item.field}`===target);
  if(question) {
    const field=String(question.field);
    if(/^(?:spouse)?(?:eca|education)/i.test(field)) return resourceHref('eca',/Date/.test(field)?'validity':/ForImmigration/.test(field)?'providers':'overview',returnTo);
    if(/TestDate/.test(field)) return resourceHref('language-tests','validity',returnTo);
    if(/OneSkillRetake/.test(field)) return resourceHref('language-tests','ielts',returnTo);
    if(/ApprovedTest/.test(field)) return resourceHref('language-tests','overview',returnTo);
    if(field==='nominationProvinceIntention') return resourceHref('pnp-overview','eligibility',returnTo);
    if(question.group==='general') return resourceHref(program??'cec','settlement',returnTo);
    if(question.group==='funds') return field==='fswValidOffer'?resourceHref('job-offer','programs',returnTo)
      :resourceHref('proof-of-funds',field==='familySize'||field==='settlementFunds'?'amounts':field==='fundsAvailable'?'documents':'exemption',returnTo);
    if(question.group==='adaptability') return resourceHref('fsw',field==='fswArrangedEmployment'?'employment':'adaptability',returnTo);
    return resourceHref(question.group==='cec'||question.group==='fsw'||question.group==='fst'?question.group:program??'cec',field==='fstValidOffer'||field==='fstCertificate'?'employment':'work',returnTo);
  }
  if(/^halo-(?:english|french|spouse|fsw-spouse)-(?:test|listening|reading|writing|speaking)$/.test(target)) {
    const chapter=['ielts','celpip','pte','tef','tcf'].includes(test??'')?test!:'overview';
    return resourceHref('language-tests',chapter,returnTo);
  }
  const locations:Record<string,[string,string]>={
    'halo-age':['crs-criteria','age-table'],
    'halo-marital':['crs-criteria','core'],'halo-spouse-situation':['crs-criteria','core'],
    'halo-education':['eca','points'],'halo-spouse-education':['eca','points'],
    'halo-education-qualification':['eca','overview'],'halo-spouse-education-qualification':['eca','overview'],
    'halo-canadian-credential':['crs-criteria','canadian-study'],'halo-credential-level':['crs-criteria','canadian-study'],
    'halo-first-language':['crs-criteria','language-table'],'halo-has-french':['crs-criteria','additional'],
    'halo-canadian-work':['crs-criteria','work-table'],'halo-foreign-work':['crs-criteria','transferability'],
    'halo-teer':['noc','teer'],'halo-nomination':['crs-criteria','additional'],'halo-sibling':['crs-criteria','additional'],
    'halo-trade-certificate':['crs-criteria','transferability'],
    'halo-spouse-language':['crs-criteria','core'],'halo-fsw-spouse-language':['fsw','adaptability'],'halo-spouse-work':['crs-criteria','core'],
    'halo-section-about':['crs-criteria','core'],'halo-section-education':['crs-criteria','education-table'],
    'halo-section-language':['crs-criteria','language-table'],'halo-section-work':['crs-criteria','work-table'],
    'halo-section-additional':['crs-criteria','additional'],'halo-section-spouse':['crs-criteria','core'],
  };
  const location=locations[target];
  if(location) return resourceHref(location[0],location[1],returnTo);
  return resourceLibraryHref(returnTo);
}

export function checkEditLocation(program:PathwayId,check:PathwayCheck):{view:'crs'|'pathways';target:string} {
  if(['language','education','documents'].includes(check.group)) {
    const section=check.group==='education'||/education|eca/i.test(check.id)?'education':'language';
    return {view:'crs',target:targets.has(`halo-condition-${check.id}`)?`halo-condition-${check.id}`:`halo-section-${section}`};
  }
  const synthetic:Record<string,string>={'cec-teer':'halo-condition-cecTeer','cec-self-employment':'halo-condition-cecNonSelfEmployed','fsw-primary-noc':'halo-condition-fswPrimaryNoc','fsw-work-consistency':'halo-condition-fswTotalMonths','fst-noc':'halo-condition-fstNoc','fst-offer-or-certificate':'halo-condition-fstValidOffer','fsw-selection':'halo-qualify-adaptability'};
  return {view:'pathways',target:synthetic[check.id]??(check.group==='funds'?'halo-qualify-funds':targets.has(`halo-condition-${check.id}`)?`halo-condition-${check.id}`:`halo-qualify-${program}`)};
}

export function checkResourceHref(program: PathwayId, check: PathwayCheck,returnTo?:string|null): string {
  const section = check.group === 'general' ? (check.id === 'assessment-date' ? 'documents' : 'settlement')
    : check.group === 'language' ? 'language'
    : check.group === 'education' ? 'education'
    : check.group === 'funds' ? 'funds'
    : check.group === 'selection' ? 'selection'
    : check.group === 'offer' ? 'employment'
    : check.group === 'documents' ? (/education|eca/i.test(check.id) ? 'education' : 'language')
    : 'work';
  return resourceHref(program, section,returnTo);
}

export function resourceIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/(?:resources|programs)\/([^/]+)\/?$/);
  if (!match) return /^\/(?:resources|programs)\//.test(pathname) ? '' : null;
  try { return decodeURIComponent(match[1]); } catch { return ''; }
}
